import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import OECheckbox from '../../../../global/oe-checkbox/oe-checkbox';
import Text from '../../../../global/text/text';
import {ReportConfigurationViewModel} from '../../_reports/report-configuration/report-configuration.viewmodel';
import './report-selected-fields.scss';
import DefaultButton from '../../../../global/default-button/default-button';
import { ProjectCSPNonceDerictives } from '../../../../../constants/common.constants';

interface IProps {
    vm: ReportConfigurationViewModel;
    intl: IntlShape;
}

const ReportSelectedFields = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;
    const enabledFields = vm.getEnabledItems();
    const filteredFields = enabledFields.filter(el => el.label.toLowerCase().includes(vm.searchValue.toLowerCase()));
    const noItemsFound = !!(enabledFields.length && !filteredFields.length);
    const isAllFieldsSelected = vm.getAvailableColumns().every(el => el.enabled);
    const isNoFieldsSelected = vm.getAvailableColumns().every(el => !el.enabled);

    let fieldsInfoLabel = '';
    if (isAllFieldsSelected) {
        fieldsInfoLabel = intl.formatMessage({
            id: 'oe.reports.allFieldsAreSelected',
            description: 'Info label',
            defaultMessage: 'All fields are selected.'
        });
    } else if (isNoFieldsSelected) {
        fieldsInfoLabel = intl.formatMessage({
            id: 'oe.reports.noFieldsAreSelected',
            description: 'Info label',
            defaultMessage: 'No fields are selected.'
        });
    }

    const onDragEnd = ({ source, destination }: DropResult): void => {
        if (!destination || source.index === destination.index) return;

        if (source.droppableId === destination.droppableId) {
            vm.reorder(source.index, destination.index);
        } else {
            vm.move(source.index, destination.index);
        }
    };

    return (
        <div className='report-selected-fields'>
            { vm.withLayout && <div className='generate-event-report__label'>
                <FormattedMessage
                    id='oe.reports.displayedColumns'
                    defaultMessage='Displayed Columns'
                    description='Label text'
                />
            </div> }
            { fieldsInfoLabel && <Text className='generate-event-report__fields-info-label' message={fieldsInfoLabel} /> }
            { !isAllFieldsSelected &&
                <DefaultButton
                    className='generate-event-report__reset-button'
                    modifier='link'
                    text={intl.formatMessage({
                        id: 'oe.reports.resetColumns',
                        description: 'Button label',
                        defaultMessage: 'Reset columns'
                    })}
                    onClick={vm.resetConfiguration}
                />
            }
            { noItemsFound && <Text
                className='report-selected-fields__no-items-found'
                id='oe.reports.noFieldsFound'
                message='No Fields found.'
                description='Empty list message'
            /> }
            <div className='report-selected-fields__list'>
                {/* TODO: Replace to common <DragDropList /> component */}
                <DragDropContext onDragEnd={onDragEnd} nonce={ProjectCSPNonceDerictives.OEC_DRAG_DROP_STYLES}>
                    <Droppable droppableId='droppable'>
                        {droppableProps => (
                            <div {...droppableProps.droppableProps} ref={droppableProps.innerRef}>
                                {filteredFields.map(field => {
                                    return (
                                        <Draggable
                                            key={field.fieldId}
                                            draggableId={field.fieldId}
                                            index={field.orderIndex}
                                        >
                                            {/* eslint-disable-next-line @typescript-eslint/no-shadow */}
                                            {(provided, snapshot) => {
                                                const { style } = provided.draggableProps;

                                                if (snapshot.isDragging) {
                                                    // @ts-ignore ignore due to "react-beautiful-dnd" library issue
                                                    style.boxShadow = '0 0 6px 0 rgba(0, 0, 0, 0.3)';
                                                }

                                                return (
                                                    <div
                                                        className='report-selected-fields__item'
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={style}
                                                    >
                                                        <OECheckbox
                                                            checked={field.enabled}
                                                            onChange={() => vm.disableField(field)}
                                                            label={<Text withEllipsis message={field.label} />}
                                                        />
                                                    </div>);
                                            }}
                                        </Draggable>
                                    );
                                })}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
}));

export default ReportSelectedFields;
